import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useContext,
} from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import ActionsMenu from 'components/actions-menu'
import NoteAddIcon from 'components/material-symbol/icons/NoteAdd'
import GroupIcon from 'components/material-symbol/icons/Group'
import LockIcon from 'components/material-symbol/icons/Lock'
import EditOutlinedIcon from 'components/material-symbol/icons/Edit'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import MoreVertIcon from 'components/material-symbol/icons/MoreVert'
import { useSnackbar } from 'notistack'
import { useSnackbarSuccess } from 'lib/snackbar'
import TipWell, { TipWellActionButton } from 'components/tip-well'
import BookingTypeIconBadge from 'pages/pga-coach/bookings/booking-types/booking-type-icon-badge'
import SessionNoteInlineForm from 'pages/pga-coach/students/student-show-page/student-notes/session-note-inline-form'
import DeleteNoteDialog from 'pages/pga-coach/students/student-show-page/student-notes/delete-note-dialog'
import SubviewPageHeader from 'pages/pga-coach/bookings/events/event-details-dialog/subview-page-header'
import {
  useGetNotes,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
} from 'pages/pga-coach/students/student-show-page/student-notes/api-hooks'
import { getLatestNote } from 'pages/pga-coach/students/student-show-page/student-notes/session-note-utils'
import { RefetchContext } from 'lib/utils/refetchContext'
import { useStyles } from './styles'

const SESSION_TIP_COPY =
  "Capture what you covered in this lesson. Notes also appear on the student's profile."

function getSessionNotes(notes, sessionId) {
  return (notes || []).filter(
    note =>
      note.notable?.__typename === 'Session' &&
      String(note.notable.id) === String(sessionId),
  )
}

function SessionNotesSection({
  sessionId,
  studentId,
  studentFirstName,
  sessionTitle,
  startDateTime,
  timezone,
  startInPanel,
  onPanelChange,
}) {
  const classes = useStyles()
  const refetchSessions = useContext(RefetchContext)
  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)

  const { data, loading, error, refetch } = useGetNotes(studentId)
  const [createNote, { loading: creating }] = useCreateNote(refetch)
  const [updateNote, { loading: updating }] = useUpdateNote(refetch)
  const [deleteNote, { loading: deleting }] = useDeleteNote(refetch)

  const [formMode, setFormMode] = useState(startInPanel ? 'pending' : null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const sessionNotes = useMemo(() => getSessionNotes(data?.notes, sessionId), [
    data?.notes,
    sessionId,
  ])
  const primaryNote = getLatestNote(sessionNotes)

  const closeForm = useCallback(() => {
    setFormMode(null)
    onPanelChange?.(false)
  }, [onPanelChange])

  const openForm = useCallback(
    mode => {
      setFormMode(mode)
      onPanelChange?.(true)
    },
    [onPanelChange],
  )

  useEffect(() => {
    if (formMode !== 'pending' || loading) return
    setFormMode(primaryNote ? 'edit' : 'create')
  }, [formMode, loading, primaryNote])

  const handleSubmit = useCallback(
    async payload => {
      try {
        if (formMode === 'edit') {
          await updateNote(payload)
          snackbarSuccess('Note updated.')
        } else {
          await createNote(payload)
          snackbarSuccess('Note created.')
          refetchSessions?.()
        }
        closeForm()
      } catch (err) {
        enqueueSnackbar(err?.message || 'Failed to save note.', {
          variant: 'error',
        })
      }
    },
    [
      formMode,
      createNote,
      updateNote,
      snackbarSuccess,
      closeForm,
      enqueueSnackbar,
      refetchSessions,
    ],
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    try {
      await deleteNote(deleteTarget.id)
      setDeleteTarget(null)
      closeForm()
      snackbarSuccess('Note deleted.')
      refetchSessions?.()
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to delete note.', {
        variant: 'error',
      })
    }
  }, [
    deleteTarget,
    deleteNote,
    snackbarSuccess,
    enqueueSnackbar,
    closeForm,
    refetchSessions,
  ])

  const isSubmitting = creating || updating
  const isShared = primaryNote ? !primaryNote.private : false
  const showPanel = formMode != null
  const composerMode = formMode === 'edit' ? 'edit' : 'create'

  if (showPanel) {
    return (
      <>
        <SubviewPageHeader
          eventTitle={sessionTitle}
          pageTitle={formMode === 'edit' ? 'Edit note' : 'Add a note'}
          onBack={closeForm}
        />
        {loading || formMode === 'pending' ? (
          <Box className={classes.notesLoading}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2">
            Failed to load notes.
          </Typography>
        ) : (
          <Box className={classes.notesPanel}>
            <SessionNoteInlineForm
              mode={composerMode}
              sessionId={sessionId}
              sessionTitle={sessionTitle}
              sessionDate={startDateTime}
              timezone={timezone}
              studentFirstName={studentFirstName}
              note={formMode === 'edit' ? primaryNote : null}
              onSubmit={handleSubmit}
              onCancel={closeForm}
              isSubmitting={isSubmitting}
              embedded={false}
              heading={null}
            />
          </Box>
        )}

        <DeleteNoteDialog
          open={Boolean(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          disabled={deleting}
        />
      </>
    )
  }

  return (
    <>
      {loading ? (
        <Box className={classes.notesCard}>
          <Box className={classes.notesLoading}>
            <CircularProgress size={24} />
          </Box>
        </Box>
      ) : error ? (
        <Box className={classes.notesCard}>
          <Typography color="error" variant="body2">
            Failed to load notes.
          </Typography>
        </Box>
      ) : primaryNote ? (
        <Box className={`${classes.notesCard} ${classes.notesCardClickable}`}>
          <Box className={classes.notesPreview}>
            <Box className={classes.notesPreviewHeader}>
              <div
                className={classes.notesActivateArea}
                onClick={() => openForm('edit')}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    openForm('edit')
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Edit session note"
              >
                <Typography className={classes.notesPreviewTitle}>
                  {primaryNote.title || sessionTitle || 'Session note'}
                </Typography>
                <Typography className={classes.notesPreviewContent}>
                  {primaryNote.content}
                </Typography>
              </div>
              <div className={classes.notesMenuWrap}>
                <ActionsMenu
                  ariaId={`session-note-${primaryNote.id}`}
                  aria-label="Note actions"
                  buttonContent={<MoreVertIcon />}
                  className={classes.notesMenuButton}
                  color="default"
                  size="small"
                >
                  <MenuItem onClick={() => openForm('edit')}>
                    <ListItemIcon className={classes.notesMenuIcon}>
                      <EditOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Edit" />
                  </MenuItem>
                  <MenuItem onClick={() => setDeleteTarget(primaryNote)}>
                    <ListItemIcon className={classes.notesMenuIcon}>
                      <DeleteOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Delete" />
                  </MenuItem>
                </ActionsMenu>
              </div>
            </Box>
            <Box className={classes.notesPreviewMeta}>
              <BookingTypeIconBadge
                label={isShared ? 'Shared with your student' : 'Private to you'}
                variant="private"
              >
                {isShared ? <GroupIcon /> : <LockIcon />}
              </BookingTypeIconBadge>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box className={classes.notesTip}>
          <TipWell
            icon={NoteAddIcon}
            caption={SESSION_TIP_COPY}
            action={
              <TipWellActionButton
                startIcon={<NoteAddIcon />}
                onClick={() => openForm('create')}
              >
                Add Note
              </TipWellActionButton>
            }
          />
        </Box>
      )}

      <DeleteNoteDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        disabled={deleting}
      />
    </>
  )
}

SessionNotesSection.propTypes = {
  sessionId: PropTypes.string.isRequired,
  studentId: PropTypes.string.isRequired,
  studentFirstName: PropTypes.string,
  sessionTitle: PropTypes.string,
  startDateTime: PropTypes.string.isRequired,
  endDateTime: PropTypes.string,
  timezone: PropTypes.string,
  startInPanel: PropTypes.bool,
  onPanelChange: PropTypes.func,
}

SessionNotesSection.defaultProps = {
  studentFirstName: null,
  sessionTitle: null,
  endDateTime: null,
  timezone: null,
  startInPanel: false,
  onPanelChange: null,
}

export default SessionNotesSection
